import { GeckoEntryBase } from 'types/entries/geckoEntry'
import { RichTextContent } from 'types/richText/types'
import {
  RolodexEntryIndexType,
  RolodexEntryJson,
  RolodexEntryType,
} from 'types/rolodex/rolodexEntries'

export enum ValueStatus {
  MODELED = 'Modeled',
  CUSTOMER_AGREED = 'Customer Agreed',
  THEORETICAL = 'Theoretical',
  UNKNOWN = 'Unknown',
}

export enum ActionStatus {
  RECOMMENDED = 'Recommended',
  PLANNED = 'Planned',
  ACTIONED = 'Actioned',
  HAPPENED_WITHOUT_GECKO = 'Happened without Gecko',
  UNKNOWN = 'Unknown',
}

export interface ValueStoryData {
  date: string
  title: string
  units: string[]
  systems: string[]
  content: RichTextContent
  value_unit: string
  value_status: ValueStatus
  action_status: ActionStatus
  value_created: string
}

export function isGeckoValueStory(entry: unknown): entry is GeckoValueStory {
  return (
    typeof entry === 'object' &&
    entry !== null &&
    'type' in entry &&
    (entry as { type: string }).type === RolodexEntryType.VALUE_STORY
  )
}

export class GeckoValueStory extends GeckoEntryBase<
  ValueStoryData & { [key: string]: unknown },
  {
    site: string
    organization: string
    sandbox?: string
  },
  RolodexEntryIndexType
> {
  public static fromJson(json: RolodexEntryJson): GeckoValueStory | undefined {
    if (json.type !== RolodexEntryType.VALUE_STORY) {
      return undefined
    }

    try {
      return new GeckoValueStory(
        json,
        json.data as ValueStoryData & { [key: string]: unknown },
        json.tags as { site: string; organization: string; sandbox?: string },
        json.index
      )
    } catch (error) {
      console.warn('Failed to create GeckoValueStory from JSON:', error)
      return undefined
    }
  }

  private constructor(
    json: RolodexEntryJson,
    data: ValueStoryData & { [key: string]: unknown },
    tags: { site: string; organization: string; sandbox?: string },
    index: RolodexEntryIndexType
  ) {
    super(json, data, tags, index)
  }

  public get valueStoryData(): ValueStoryData {
    return this.data
  }
  public get title(): string {
    return this.data.title || 'Untitled'
  }
  public get date(): string {
    return this.data.date || ''
  }
  public get valueStatus(): string {
    return this.data.value_status || ''
  }
  public get actionStatus(): string {
    return this.data.action_status || ''
  }
  public get valueCreated(): string {
    return this.data.value_created || ''
  }
  public get valueMetric(): string {
    return this.data.value_unit || ''
  }

  public get unitsText(): string {
    return this.data.units?.join(', ') || ''
  }

  public get systemsText(): string {
    return this.data.systems?.join(', ') || ''
  }
}
